import { motion, useReducedMotion } from "motion/react";
import { BookMarked } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";
import { Ziggy } from "../../Ziggy";
import { EASE_OUT } from "../../../lib/motion";
import type { ZiggyAge } from "../../../lib/ziggy";
import { useLearnLocale } from "../../../lib/i18n/useLearnLocale";

const COVER_PALETTE = [
  { bg: "from-slate-700 to-slate-900", accent: "border-slate-500/40" },
  { bg: "from-emerald-700 to-emerald-950", accent: "border-emerald-500/40" },
  { bg: "from-indigo-700 to-indigo-950", accent: "border-indigo-400/40" },
  { bg: "from-amber-700 to-amber-950", accent: "border-amber-500/40" },
] as const;

function BookSpine({ title, index }: { title: string; index: number }) {
  const palette = COVER_PALETTE[index % COVER_PALETTE.length] ?? COVER_PALETTE[0];
  const reduce = useReducedMotion();

  return (
    <motion.li
      initial={reduce ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: reduce ? 0 : index * 0.06, duration: 0.32, ease: EASE_OUT }}
      className="w-[9.5rem] shrink-0 snap-start sm:w-[10.5rem]"
    >
      <div
        className={cn(
          "relative flex h-[12.5rem] flex-col overflow-hidden rounded-lg border bg-gradient-to-br shadow-md",
          palette.bg,
          palette.accent,
        )}
      >
        <div className="absolute inset-y-0 left-0 w-2 bg-black/25" aria-hidden />
        <div className="flex flex-1 flex-col justify-between p-3 pl-4">
          <BookMarked className="size-4 text-white/50" aria-hidden />
          <p className="line-clamp-5 text-left text-[0.72rem] font-semibold leading-snug text-white/95">
            {title}
          </p>
        </div>
      </div>
    </motion.li>
  );
}

/**
 * Clean reading shelf — horizontal snap scroll on a wooden shelf (SC-20d).
 */
export function RoadmapBooksHero({
  titles,
  ziggyAge = "grown",
}: {
  titles: string[];
  ziggyAge?: ZiggyAge;
}) {
  const reduce = useReducedMotion();
  const { strings } = useLearnLocale();

  if (titles.length === 0) return null;

  return (
    <section className="border-border/60 dark:to-background overflow-hidden rounded-2xl border bg-gradient-to-b from-amber-50/80 to-amber-100/40 shadow-sm dark:from-amber-950/20">
      <div className="px-4 pb-4 pt-4 sm:px-5 sm:pb-5 sm:pt-5">
        <div className="mb-4 flex items-start gap-3">
          <Ziggy age={ziggyAge} pose="reading" size={52} glow alt="" className="shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-display text-base font-semibold">{strings.recommendedReads}</p>
            <p className="text-muted-foreground mt-0.5 text-sm">{strings.recommendedReadsHint}</p>
          </div>
        </div>

        <div className="relative">
          <ul
            className="flex snap-x snap-mandatory list-none gap-3 overflow-x-auto overscroll-x-contain pb-3 pl-0.5 pr-1 [-ms-overflow-style:none] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-amber-800/25 [&::-webkit-scrollbar]:h-1.5"
            style={{ scrollBehavior: reduce ? "auto" : "smooth" }}
          >
            {titles.map((title, i) => (
              <BookSpine key={`${title}-${i}`} title={title} index={i} />
            ))}
          </ul>
          {/* wooden shelf lip */}
          <div
            className="h-3 rounded-b-md bg-gradient-to-b from-amber-700 to-amber-900 shadow-inner"
            aria-hidden
          />
        </div>
      </div>
    </section>
  );
}
